import { positionToChain } from "@/functions/getFormation";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";

import {
  MetaRating,
  getMetaRatings,
} from "../../../../../api/privateRequests/squad-builder/get-meta-ratings";
import { useOutsideClick } from "../../../../../hooks/useOutsideClick";
import {
  Focus,
  PlayerRole,
  getRolesPlusOrPlusPlus,
} from "../../../../../redux/players/playersSlice";
import {
  SquadBuilderMode,
  SquadBuilderPlayerType,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { PlusPlus } from "../../../../molecules/PlusPlus/PlusPlus";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import { PanelContainer } from "../../components/PanelContainer/PanelContainer";
import { PlayerFocus } from "../../components/PlayerRole/PlayerFocus/PlayerFocus";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  mode: SquadBuilderMode;
  player: SquadBuilderPlayerType;
  handleClickedClose: () => void;
  handleRoleChanged: (
    playerRoleId: number,
    metaRatings?: MetaRating[],
    focus?: string,
  ) => void;
  changingPitchItemOnIndex: number;
  pitchIsFull: boolean;
  availablePlayerRoles: Record<string, boolean>;
  playerRoles: PlayerRole[];
  chemistry?: number;
  selectedPlayerRoleId?: number;
  selectedFocus?: string;
  handlePositionAndRoleChanged?: (
    position: string,
    roleId: number,
    metaRatings: MetaRating[],
  ) => void;
  isBench: boolean;
};

export const positionsInSections = Object.entries(positionToChain).reduce(
  (acc, [key, value]) => {
    if (value === "attackers") {
      acc.attackers.push(key);
    } else if (value === "midfielders") {
      acc.midfielders.push(key);
    } else if (value === "defenders") {
      acc.defenders.push(key);
    } else if (value === "goalkeepers") {
      acc.goalkeepers.push(key);
    }
    return acc;
  },
  { attackers: [], midfielders: [], defenders: [], goalkeepers: [] },
);

const Positions = ({
  selectedPosition,
  handleSelectPosition,
}: {
  selectedPosition: string;
  handleSelectPosition: (position: string) => void;
}) => {
  const { attackers, midfielders, defenders, goalkeepers } =
    positionsInSections;

  return (
    <ComponentContainer title="Position" marginTop="mt-8">
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col">
          <p className="text-xs">Attackers</p>
          <div className="flex flex-wrap gap-1">
            {attackers.map((attacker) => (
              <ButtonForPlayerRoleOrFocus
                key={attacker}
                active={selectedPosition === attacker}
                onClick={() => handleSelectPosition(attacker)}
                fixedWidth="w-[78px] sb-v2-lg:w-[80px]"
              >
                {attacker}
              </ButtonForPlayerRoleOrFocus>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">Midfielders</p>
          <div className="flex flex-wrap gap-1">
            {midfielders.map((midfielder) => (
              <ButtonForPlayerRoleOrFocus
                key={midfielder}
                active={selectedPosition === midfielder}
                onClick={() => handleSelectPosition(midfielder)}
                fixedWidth="w-[78px] sb-v2-lg:w-[80px]"
              >
                {midfielder}
              </ButtonForPlayerRoleOrFocus>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">Defenders</p>
          <div className="flex flex-wrap gap-1">
            {defenders.map((defender) => (
              <ButtonForPlayerRoleOrFocus
                key={defender}
                active={selectedPosition === defender}
                onClick={() => handleSelectPosition(defender)}
                fixedWidth="w-[78px] sb-v2-lg:w-[80px]"
              >
                {defender}
              </ButtonForPlayerRoleOrFocus>
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">Goalkeeper</p>
          <div className="flex flex-wrap gap-1">
            {goalkeepers.map((goalkeeper) => (
              <ButtonForPlayerRoleOrFocus
                key={goalkeeper}
                active={selectedPosition === goalkeeper}
                onClick={() => handleSelectPosition(goalkeeper)}
                fixedWidth="w-[78px] sb-v2-lg:w-[80px]"
              >
                {goalkeeper}
              </ButtonForPlayerRoleOrFocus>
            ))}
          </div>
        </div>
      </div>
    </ComponentContainer>
  );
};

export const PlayerRoleMode = ({
  mode,
  player,
  handleClickedClose,
  handleRoleChanged,
  changingPitchItemOnIndex,
  playerRoles,
  chemistry,
  // position,
  selectedFocus: selectedFocusId,
  selectedPlayerRoleId,
  handlePositionAndRoleChanged,
  isBench,
}: Props) => {
  const ref = useOutsideClick(() => handleClickedClose());
  const [selectedPlayerRole, setSelectedPlayerRole] = useState<PlayerRole>();
  const [selectedFocus, setSelectedFocus] = useState<Focus>();
  const [allPlayerMetaRatings, setAllPlayerMetaRatings] = useState(
    [] as MetaRating[],
  );
  const [playerRolesToMetaRating, setPlayerRolesToMetaRatings] = useState<
    Record<number, number>
  >({});
  const position = useMemo(() => {
    return playerRoles.find((pr) => pr.id === selectedPlayerRoleId)?.position;
  }, [selectedPlayerRoleId]);
  const findPlayerRoleForPosition = (position: string) => {
    return playerRoles.filter((playerRole) => playerRole.position === position);
  };

  const handleSelectPositionForBench = (position: string) => {
    const playerRole = findPlayerRoleForPosition(position);
    setSelectedPlayerRole(playerRole[0]);
    const metaRatings = allPlayerMetaRatings.filter(
      (mr) => mr.playerRoleId === playerRole[0].id,
    );
    handlePositionAndRoleChanged(position, playerRole[0].id, metaRatings);
  };

  useEffect(() => {
    const localSelectedPlayerRole = playerRoles.find(
      (playerRole) =>
        playerRole.id === selectedPlayerRoleId ||
        playerRole.id === player?.playerRoleId,
    );
    setSelectedPlayerRole(localSelectedPlayerRole);

    setSelectedFocus(
      localSelectedPlayerRole?.focuses.find(
        (focus) => focus.name === selectedFocusId,
      ) || localSelectedPlayerRole?.focuses[0],
    );
  }, [selectedPlayerRoleId, selectedFocusId, playerRoles]);

  useEffect(() => {
    const fetchMetaRatings = async () => {
      try {
        let resMetaRatingsForPlayer = await getMetaRatings({
          resourceId: player?.resourceId,
          evoPathId: player?.evoPathId,
          evoPath: player?.evolutionPath?.join("-"),
        });

        setAllPlayerMetaRatings(resMetaRatingsForPlayer);
        const tempPlayerRoleToMetaRating: Record<string, number> = {};
        let tempBestMetaRating = 0;
        resMetaRatingsForPlayer.forEach((metaRating) => {
          if (tempBestMetaRating < metaRating.metaRating) {
            tempBestMetaRating = metaRating.metaRating;
          }
          if (metaRating.chemistry === chemistry) {
            tempPlayerRoleToMetaRating[metaRating.playerRoleId] =
              metaRating.metaRating;
          }
        });
        setPlayerRolesToMetaRatings(tempPlayerRoleToMetaRating);
      } catch (error) {}
    };
    if (mode === "default" && player?.resourceId && player?.playerRoleId) {
      fetchMetaRatings();
    } else {
      setAllPlayerMetaRatings([]);
      setPlayerRolesToMetaRatings({});
    }
  }, [
    player?.resourceId,
    player?.playerRoleId,
    player?.evoPathId,
    player?.evolutionPath,
    position,
    chemistry,
    mode,
    changingPitchItemOnIndex,
  ]);

  const selectPlayerRoleOrFocus = ({
    playerRoleId,
    focus,
  }: {
    playerRoleId?: number;
    focus?: string;
  }) => {
    const metaRatings = allPlayerMetaRatings.filter(
      (mr) => mr.playerRoleId === playerRoleId,
    );
    if (isBench) {
      handlePositionAndRoleChanged(player?.position, playerRoleId, metaRatings);
      const playerRole = playerRoles.find((pr) => pr.id === playerRoleId);
      setSelectedPlayerRole(playerRole);
    } else {
      handleRoleChanged(playerRoleId, metaRatings, focus);
    }
  };

  let focuses = selectedPlayerRole?.focuses?.filter((focus) =>
    focus.positions.includes(position),
  );
  return (
    <div ref={ref}>
      <PanelHeader
        title={isBench ? "Player Role and Position" : "Add Player Role"}
        onClose={handleClickedClose}
      />
      <PanelContainer>
        {isBench && (
          <Positions
            selectedPosition={player?.position}
            handleSelectPosition={handleSelectPositionForBench}
          />
        )}
        <ComponentContainer
          marginTop="mt-8"
          title={
            <div className="flex flex-col whitespace-nowrap gap-x-1">
              <span>{"Player Role"}</span>
              <span className="text-xs text-gray-200">
                Impacts our AI suggestions ✨
              </span>
            </div>
          }
        >
          <div className="grid items-center w-full grid-cols-1 gap-1">
            {playerRoles
              .filter((pr) => pr.position === position)
              .map((pr) => {
                const isRolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
                  playerRoleId: pr.id,
                  rolesPlus: player.rolesPlus,
                });

                return (
                  <ButtonForPlayerRoleOrFocus
                    key={pr.name}
                    active={selectedPlayerRole?.id === pr.id}
                    onClick={(e) => {
                      selectPlayerRoleOrFocus({
                        playerRoleId: pr.id,
                      });
                      e.stopPropagation();
                    }}
                  >
                    <div className="flex flex-row justify-between px-2">
                      <span>{pr.name}</span>
                      <div className="flex flex-row gap-x-2">
                        <PlusPlus
                          plus={isRolesPlusOrPlusPlus === "plus"}
                          plusPlus={isRolesPlusOrPlusPlus === "plusPlus"}
                          withBackground={true}
                        />
                        <span>
                          {playerRolesToMetaRating?.[pr.id]?.toFixed(1) || ""}
                        </span>
                      </div>
                    </div>
                  </ButtonForPlayerRoleOrFocus>
                );
              })}
          </div>
          <div className="min-h-[120px]">
            <p className="pt-3 text-xs text-white">Role Description</p>
            <p className="pt-1 text-xs text-gray-300">
              {selectedPlayerRole?.description}
            </p>
          </div>
        </ComponentContainer>

        {!isBench && (
          <ComponentContainer marginTop="mt-3" title={"Focus"}>
            <div className="grid items-center w-full grid-cols-3 desktop:grid-cols-2 gap-[10px] sb-v2-lg:gap-4">
              {focuses?.map((focus, index) => (
                <div
                  key={focus.name}
                  className={clsx(
                    focuses.length === index + 1 &&
                      index % 2 === 0 &&
                      "desktop:col-span-2 desktop:mx-auto",
                  )}
                >
                  <PlayerFocus
                    position={position}
                    playerRoleId={selectedPlayerRole?.id}
                    focus={focus.name}
                    active={focus.name === selectedFocus?.name}
                    onClick={() => {
                      selectPlayerRoleOrFocus({
                        playerRoleId: selectedPlayerRole?.id,
                        focus: focus.name,
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="min-h-[120px]">
              <p className="pt-3 text-xs text-white">Focus Description</p>
              <p className="pt-1 text-xs text-gray-300">
                {selectedFocus?.description}
              </p>
            </div>
          </ComponentContainer>
        )}
      </PanelContainer>
    </div>
  );
};

const ButtonForPlayerRoleOrFocus = ({
  active,
  onClick,
  children,
  fixedWidth,
}: {
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  children: React.ReactNode;
  fixedWidth?: string;
}) => (
  <button
    className={clsx(
      "rounded shadow-button text-sm h-7 whitespace-nowrap",
      active ? "bg-primary-blue text-white" : "bg-gray-200 text-black",
      fixedWidth || "w-full",
    )}
    onClick={onClick}
  >
    {children}
  </button>
);
